/**
 * AI Controller — per-player state machine for AI opponents.
 *
 * One instance per AI player. Created at game start.
 * update() called every fixed frame, routes to phase-specific handlers.
 *
 * Input injection: AIController.input is an AIInput instance.
 * The main game loop passes it as the InputProvider for AI players.
 */

import type { GameState } from '../types';
import { AIInput } from './ai-input';
import { getSkillProfile, type SkillProfile } from './skill-profile';
import {
  type AimState,
  createAimState,
  resetAimState,
  updateAimPhase,
} from './phase-aiming';
import {
  type ReactiveState,
  createReactiveState,
  initReactiveState,
  updateAIDI,
  updateAIBurst,
  updateAIEdgeguard,
  updateAIDefense,
} from './phase-reactive';

/** Reactive phases where AI needs decide-once + reaction delay. */
const REACTIVE_PHASES = new Set(['di_window', 'burst_window', 'edgeguard', 'defensive_movement']);

/** Get the active player index for a reactive phase. */
function getActivePlayerForPhase(state: GameState, phase: string): number {
  switch (phase) {
    case 'di_window': return state.diActivePlayer;
    case 'burst_window': return state.burstActivePlayer;
    case 'defensive_movement': return state.defenseActivePlayer;
    case 'edgeguard': return state.currentPlayer;
    default: return -1;
  }
}

export class AIController {
  readonly playerIndex: number;
  readonly input: AIInput;
  readonly profile: SkillProfile;

  private aimState: AimState;
  private reactiveState: ReactiveState;
  private lastPhase: string = '';
  private lastActivePlayer: number = -1;
  private hasFired: boolean = false;

  constructor(playerIndex: number, difficulty: number) {
    this.playerIndex = playerIndex;
    this.input = new AIInput();
    this.profile = getSkillProfile(difficulty);
    this.aimState = createAimState();
    this.reactiveState = createReactiveState();
  }

  /**
   * Main per-frame update. Called during fixed update for:
   * - Active player phases (aiming, firing)
   * - Reactive player phases (DI, burst, edgeguard, defense)
   */
  update(state: GameState): void {
    const phase = state.phase;
    const activePlayer = REACTIVE_PHASES.has(phase)
      ? getActivePlayerForPhase(state, phase)
      : -1;

    // Detect phase or active-player transitions
    if (phase !== this.lastPhase || activePlayer !== this.lastActivePlayer) {
      // Reset aim state on new aiming phase
      if (phase === 'aiming' && state.currentPlayer === this.playerIndex) {
        resetAimState(this.aimState, this.profile);
        this.hasFired = false;
        this.input.clear();
      }

      // Init reactive state for reactive phases where this AI is active
      if (REACTIVE_PHASES.has(phase) && activePlayer === this.playerIndex) {
        initReactiveState(this.reactiveState, state, phase, this.playerIndex, this.profile);
      }

      this.lastPhase = phase;
      this.lastActivePlayer = activePlayer;
    }

    switch (phase) {
      case 'aiming':
        if (state.currentPlayer === this.playerIndex && !this.hasFired) {
          const fired = updateAimPhase(
            this.aimState, state, this.playerIndex, this.input, this.profile,
          );
          if (fired) this.hasFired = true;
        }
        break;

      case 'di_window':
        if (state.diActivePlayer === this.playerIndex) {
          updateAIDI(state, this.playerIndex, this.input, this.profile, this.reactiveState);
        }
        break;

      case 'burst_window':
        if (state.burstActivePlayer === this.playerIndex) {
          updateAIBurst(state, this.playerIndex, this.input, this.profile, this.reactiveState);
        }
        break;

      case 'edgeguard':
        if (state.currentPlayer === this.playerIndex) {
          updateAIEdgeguard(state, this.playerIndex, this.input, this.profile, this.reactiveState);
        }
        break;

      case 'defensive_movement':
        if (state.defenseActivePlayer === this.playerIndex) {
          updateAIDefense(state, this.playerIndex, this.input, this.profile, this.reactiveState);
        }
        break;

      case 'round_start':
        // Clear inputs — AI waits for round start to complete
        this.input.clear();
        break;

      default:
        // Other phases — no AI input needed
        break;
    }
  }

  /** Reset for a new round. */
  resetForRound(): void {
    this.input.clear();
    resetAimState(this.aimState, this.profile);
    this.hasFired = false;
    this.lastPhase = '';
    this.lastActivePlayer = -1;
    this.reactiveState = createReactiveState();
  }
}
